import { User } from '../../@types';
import { NavBarData } from '../../reducers/navBar/data';
import { getProductAccess, IPA_NEGOTIATION_HOME_PATH, IPA_PRO_HOME_PATH, isIpaBasicPackage, isProductRule } from '../../utils/ipaAccess';
import { NavBarBadgeProps } from './NavBarBadge/NavBarBadge';

export const checkIsFree = (user?: User) => {
  return user?.servicoPanel?.pacotePainel.name === 'FREE';
};

export const checkIsDisabled = (key: NavOptionKeys, user?: User) => {
  return !user?.servicos?.some(({ name }) => name === key);
};

export const checkHasService = (key: NavOptionKeys, user?: User) => {
  return !!user?.servicosCliente?.some(({ name }) => name === key);
};

export const checkHasCustomizedPannels = (user?: User) => {
  return !!(user?.servicos?.some(item => item.name === 'BI') && (user?.servicoBi?.analises?.length || user?.servicoBi?.graficos?.length));
};

export type NavOptionKeys = 'PAN' | 'COL' | 'IPA' | 'NEG' | 'SDD' | 'BI' | 'IDT' | 'IRA';

export type NavOption = {
  key: NavOptionKeys;
  id: string;
  to: string;
  label: string;
  panel: string;
  skin: NavBarBadgeProps['skin'];
  isDisabled?: boolean;
  isFree?: boolean;
  hasService?: boolean;
};

export const PRICING_LABEL = 'Precificação';
export const NEGOTIATION_LABEL = 'Negociação';
export const LEGACY_PRICING_LABEL = 'Software de precificação';

const ipaNavOptions = (isFree: boolean, user?: User): NavOption[] => {
  if (!isProductRule(user)) {
    return [
      {
        key: 'IPA',
        id: isFree ? 'pricing-software-navbar-link-free' : 'pricing-software-navbar-link',
        to: isIpaBasicPackage(user) ? IPA_NEGOTIATION_HOME_PATH : IPA_PRO_HOME_PATH,
        label: LEGACY_PRICING_LABEL,
        panel: 'IPA',
        skin: 'purple',
        isFree,
        isDisabled: checkIsDisabled('IPA', user),
        hasService: checkHasService('IPA', user),
      },
    ];
  }

  const access = getProductAccess(user);
  return [
    {
      key: 'IPA',
      id: isFree ? 'pricing-software-navbar-link-free' : 'pricing-software-navbar-link',
      to: IPA_PRO_HOME_PATH,
      label: PRICING_LABEL,
      panel: 'IPA',
      skin: 'purple',
      isFree,
      isDisabled: !access.hasPricing,
      hasService: access.clientHasPricing,
    },
    {
      key: 'NEG',
      id: 'negotiation-navbar-link',
      to: IPA_NEGOTIATION_HOME_PATH,
      label: NEGOTIATION_LABEL,
      panel: 'IPA',
      skin: 'purple',
      isFree,
      isDisabled: !access.hasNegotiation,
      hasService: access.clientHasNegotiation,
    },
  ];
};

export const navOptions = (user?: User): NavOption[] => {
  const isFree = checkIsFree(user);

  return [
    {
      key: 'PAN',
      id: isFree ? 'infopanel-free-navbar-link' : 'infopanel-navbar-link',
      to: isFree ? '/isa/painel-geral-free' : '/isa/painel-geral',
      label: 'InfoPanel',
      panel: 'ISA',
      skin: 'blue',
      isFree,
      isDisabled: checkIsDisabled('PAN', user),
      hasService: checkHasService('PAN', user),
    },
    {
      key: 'COL',
      id: isFree ? 'mpdv-navbar-link-free' : 'mpdv-navbar-link',
      to: '/isa/historico-preco',
      label: 'Monitoramento PDV',
      panel: 'ISA',
      skin: 'blue',
      isFree,
      isDisabled: checkIsDisabled('COL', user),
      hasService: checkHasService('COL', user),
    },
    ...ipaNavOptions(isFree, user),
    {
      key: 'SDD',
      id: isFree ? 'simulator-navbar-link-free' : 'simulator-navbar-link',
      to: '/ipa/calculadora-demanda',
      label: 'Simulador de demanda',
      panel: 'IPA',
      skin: 'purple',
      isFree,
      isDisabled: checkIsDisabled('SDD', user),
      hasService: checkHasService('SDD', user),
    },
    ...(user?.codigoCliente === 286852
      ? [
          {
            key: 'IDT',
            id: 'industry-navbar-link',
            to: '/ipa/plataforma-editar-tabela-base',
            label: 'Indústria',
            panel: 'IPA',
            skin: 'purple',
          } as NavOption,
        ]
      : []),
  ];
};

// Identificadores do menu de produtos que a suite de tela le.
export const PRODUCT_MENU_TEST_IDS: Partial<Record<NavOptionKeys, string>> = {
  IPA: 'product-menu-pricing',
  NEG: 'product-menu-negotiation',
  SDD: 'product-menu-demand-simulator',
};

export const isNavigationBlocked = ({ key, isDisabled }: Pick<NavOption, 'key' | 'isDisabled'>) => !!isDisabled && (key === 'IPA' || key === 'NEG');

export const sortByEnabledFirst = (a: NavOption, b: NavOption) => {
  return Number(a.isDisabled) - Number(b.isDisabled);
};

export const findNavBarPayloadByKey = (key: NavOptionKeys, user?: User): NavBarData | null => {
  const option = navOptions(user).find(option => option.key === key);
  if (!option) return null;
  const { label, panel, isFree } = option;
  return { label, panel, isFree };
};
